import React, {useCallback, useEffect, useRef, useState} from 'react';
import MainLayout from '../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {Theme} from '../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Icon from '../../../components/icon';
import {tr} from '../../../translations';
import {getByScreenSize, hdp, wdp} from '../../../utils/responsive';
import QuestionInput from '../../../components/questionInput';
import GenericButton from '../../../components/button';
import {staticRecommendations} from '../../../assets/static/tabsData';
import screens from '../../../config/screens';
import {getHomeTabItemById} from '../../../utils/helpers';
import {showFiltersModal} from '../../../connected-components/filtersModal/actions';
import {RootState} from '../../../redux/store';
import {connect, useDispatch} from 'react-redux';
import {LatestScreenProps} from '../../../interfaces/screens/latest';
import Animated, {FadeInDown, FadeOutDown} from 'react-native-reanimated';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import {Platform} from 'react-native';
import '../../../utils/webAnimations';
import {getNews} from './actions';
import {useFocusEffect} from '@react-navigation/native';
import userVoice from '../../../utils/services/userVoice';
import {
  AnalyticsEvents,
  modalTypes,
  NewsOptionTab,
  TabIds,
  ToastType,
} from '../../../utils/enums';
import {setIsTabVoiceInteractionPlayed} from '../settings/reducer';
import {analyticsService} from '../../../utils/services/analytics';
import CircleItem from '../../../components/circleItem';
import dayjs, {Dayjs} from 'dayjs';
import Config from 'react-native-config';
import {FilterType} from '../../../interfaces/reducers/homeState';
import {Space} from '../home';
import useChatVoiceRecognition from '../../../utils/hooks/useChatVoiceRecognition';
import {openQuestionModal} from '../../../connected-components/questionModal/actions';
import {LinearGradient} from 'react-native-linear-gradient';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';

const Container = styled.View`
  width: 100%;
  height: 100%;
  padding-top: 4%;
`;
const BottomTabContainer = styled(GenericButton)`
  width: 90%;
  align-self: center;
  margin-top: 5%;
`;
const Row = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding-top: 4%;
`;
const SubmitButton = styled(GenericButton)`
  width: ${wdp(35)}px;
  opacity: ${({disabled}) => (disabled ? 0.6 : 1)};
  margin-right: ${getByScreenSize(wdp(0), wdp(10))}px;
`;
const RecordIcon = styled(Icon)``;
const FilterIcon = styled(Icon)``;
const AttachmentIcon = styled(Icon)``;
const IconContainer = styled.TouchableOpacity`
  background-color: ${({theme}) => theme.text.light};
  border-radius: 10px;
  height: ${({theme}) => theme.text.s2}px;
  aspect-ratio: 1;
  align-items: center;
  justify-content: center;
  margin-left: 2%;
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};
`;
const PaddingView = styled.View`
  padding-horizontal: 4%;
`;
const OptionsContainer = styled.View`
  margin-top: -${wdp(7)}px;
  width: 100%;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;

/**
 * TabNews screen component.
 *
 * @param {LatestScreenProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const TabNews: React.FC<any> = ({
  navigation,
  route,
  auth,
  homeTabs,
  settings,
}: LatestScreenProps) => {
  const theme = useTheme() as Theme;
  const {currentUser} = auth || {};
  const {tabs} = homeTabs || {};
  const {isTabVoicePlayed} = settings || {};
  const dispatch = useDispatch();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {
    id: tab_id,
    tab_name: tabName,
    filters: prevTabFilters,
    is_recommendation,
    filters_recommendations,
    filters_news_details,
    latest_summary_date,
    last_summary_dates,
  } = route?.params || {};
  const headerHeight = useCustomHeaderHeight();
  const [question, setQuestion] = useState('');
  const {startListening, stopListening, isRecording} =
    useChatVoiceRecognition(setQuestion);
  const [filtersResult, setFiltersResult] = useState<any>({});
  const [tabFilters, setTabFilters] = useState<FilterType[]>(
    prevTabFilters || [],
  );
  const [newsFilters, setNewsFilters] = useState<FilterType[]>(
    filters_news_details || [],
  );
  const [recommendationsFilters, setRecommendationsFilters] = useState<
    FilterType[]
  >(filters_recommendations || []);
  const [isError, setIsError] = useState(false);
  const [newsCount, setNewsCount] = useState('0');
  const [isLoading, setIsLoading] = useState(true);
  const [isRecommendation, setIsRecommendation] = useState(is_recommendation);
  const [summaryDateInfo, setSummaryDateInfo] = useState<{
    latestSummaryDate: Dayjs;
    lastSummaryDates: Dayjs[];
  }>({
    latestSummaryDate: latest_summary_date
      ? dayjs(latest_summary_date)
      : dayjs(),
    lastSummaryDates: last_summary_dates
      ? last_summary_dates.map(i => dayjs(i))
      : [],
  });
  const [isFirstTime, setIsFirstTime] = useState(true);
  const entryTime = useRef(new Date());
  const staticRecommendation =
    staticRecommendations[
      +(tab_id || '') as keyof typeof staticRecommendations
    ];

  const isAppleEmail =
    currentUser?.email?.trim().toLowerCase() === Config.APPLE_EMAIL;
  const optionsList = isAppleEmail
    ? []
    : //TODO Ask for this
      +(tab_id || '') === TabIds.MedicalJobOpportunities
      ? [NewsOptionTab.Analysis, NewsOptionTab.Predictions]
      : [
          NewsOptionTab.Summaries,
          NewsOptionTab.Analysis,
          NewsOptionTab.Predictions,
        ];

  useFocusEffect(
    useCallback(() => {
      return () => {
        const timeSpent = entryTime
          ? (new Date().getTime() - entryTime?.current?.getTime()) / 1000
          : 0;

        analyticsService?.logEvent(AnalyticsEvents.OpenTab, {
          tabId: tab_id,
          tabName,
          timeSpent,
        });
      };
    }, []),
  );

  const initPage = async (withoutSearch?: boolean) => {
    setIsError(false);
    setIsLoading(true);
    const res = await getNews({
      tab_id,
      keywords: withoutSearch ? '' : question || '',
      pageSize: 1,
      ...filtersResult,
    });
    setIsLoading(false);
    if (!res) {
      setIsError(true);
      setNewsCount('0');
      return;
    }
    if (
      (question || '').trim() !== '' &&
      res?.data?.length === 0 &&
      !withoutSearch
    ) {
      openQuestionModal({
        customHeaderComponent: () => (
          <Icon
            name={'multitrack-audio'}
            size={theme.text.s1 * 2}
            color={theme.secondary}
          />
        ),
        message: tr('search.empty'),
        type: modalTypes.question,
        confirmText: tr('latest.goToFilters'),
        onConfirm: () => {
          setQuestion('');
          initPage(true);
          openFilterModal();
        },
        onReject: () => {
          setQuestion('');
          setFiltersResult({});
          initPage(true);
        },
      });
    }
    // initTabInfo();
    setNewsCount(res?.total + '' || '0');
  };

  const initTabInfo = () => {
    const homeTabItem = getHomeTabItemById(tab_id);
    setNewsFilters(homeTabItem?.filters_news_details || []);
    setRecommendationsFilters(homeTabItem?.filters_recommendations || []);
    setTabFilters(homeTabItem?.filters || []);
    setIsRecommendation(!!homeTabItem?.is_recommendation);
    const lastSummaryDates = (homeTabItem?.last_summary_dates || [])
      .map(date => dayjs(date))
      .sort((a, b) => a.diff(b));
    const latestSummaryDate = homeTabItem?.latest_summary_date
      ? dayjs(homeTabItem?.latest_summary_date)
      : dayjs();
    setSummaryDateInfo({
      latestSummaryDate,
      lastSummaryDates,
    });
  };

  useEffect(() => {
    initTabInfo();
  }, [tabs]);

  useEffect(() => {
    if (Object.keys(prevTabFilters || {}).length === 0) {
      showFiltersModal({
        filters: tabFilters || [],
        filtersValues: {},
        onSubmit: val => setFiltersResult(val),
      });
    }
  }, [prevTabFilters]);

  useEffect(() => {
    if (isFirstTime && Object.keys(tabFilters || {}).length > 0) {
      showFiltersModal({
        filters: tabFilters || {},
        filtersValues: {},
        onSubmit: val => setFiltersResult(val),
      });
      setIsFirstTime(false);
    }
  }, [tabFilters]);

  useEffect(() => {
    initPage();
  }, [filtersResult, question]);

  useEffect(() => {
    setFiltersResult({});
  }, [currentUser?.specialty_id]);

  useEffect(() => {
    return () => {
      userVoice?.stop();
    };
  }, []);

  /**
   * Starts listening for voice input from the user.
   */
  const startVoiceListening = async () => {
    if (!isTabVoicePlayed) {
      dispatch(setIsTabVoiceInteractionPlayed());
    }
    showGlobalToast({message: tr('record.startRecord'), type: ToastType.HELP});
    startListening();
  };

  const onRecordHandler = async () => {
    if (isRecording) {
      userVoice?.stop();
      stopListening();
    } else {
      if (isTabVoicePlayed) {
        startVoiceListening();
      } else {
        userVoice?.play(() => startVoiceListening());
      }
    }
  };

  const onBottomActionPress = () => {
    const navigationParams = staticRecommendation
      ? {staticRecommendation}
      : {
          tabId: tab_id,
          filtersResult,
          isRecommendationByTab: true,
          recommendationsFilters,
        };
    // navigation?.navigate(screens.content.news, navigationParams);
  };

  const openFilterModal = () => {
    if (tabFilters === null) {
      return;
    }
    showFiltersModal({
      filters: tabFilters || {},
      filtersValues: filtersResult,
      onSubmit: val => setFiltersResult(val),
    });
  };

  const onOptionTabPress = (type: string) => {
    navigation?.navigate(screens.content.summaries, {
      tabId: tab_id,
      // tabFilters,
      filters: filtersResult,
      summaryDateInfo,
      isSummary: type,
      type,
    });
  };

  const _renderOptionItem = (item: string, index: number) => {
    return (
      <CircleItem
        key={item}
        isLoading={isLoading}
        label={tr(`latest.${item}`)}
        onPress={() => onOptionTabPress(item)}
        style={{
          width: wdp(28),
          marginTop: index === 1 && optionsList.length > 2 ? wdp(28) / 2 : 0,
        }}
      />
    );
  };

  const onSeeAllPressHandler = () => {
    if (isError) {
      initPage();
      return;
    }
    navigation?.navigate(screens.content.tabNews, {
      filtersResult,
      tabId: tab_id,
      search: question,
      filters: tabFilters,
      newsFilters,
    });
  };

  return (
    <MainLayout
      overlayColor={theme.homeBackground + '88'}
      backHeader
      menuHeader
      isSearch
      onSearchPress={() => {
        if (tabFilters === null) {
          return;
        }
        navigation?.navigate(screens.content.search, {
          tabId: tab_id,
          filters: tabFilters,
          filtersResult,
        });
      }}
      noPadding
      noPaddingHeader>
      <LinearGradient
        colors={[
          theme.tabNews.startLinearGradient,
          theme.tabNews.secondLinearGradient,
          theme.tabNews.thirdLinearGradient,
        ]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 1}}
        style={{
          flex: 1,
          paddingTop: headerHeight,
        }}>
        <Container>
          <PaddingView>
            <QuestionInput
              value={question}
              onChangeText={text => setQuestion(text)}
            />
            <Row>
              <SubmitButton
                isSmall
                label={tr('latest.submit')}
                onPress={() => initPage()}
                outline={(question || '').trim() === '' || isLoading}
                backgroundColor={
                  (question || '').trim() === '' || isLoading
                    ? theme.cardBackground
                    : undefined
                }
                disabledNoOpacity={(question || '').trim() === '' || isLoading}
              />
              <IconContainer style={{opacity: 0}} disabled>
                <AttachmentIcon
                  name={'attachment'}
                  size={theme.text.s55}
                  color={theme.secondary}
                />
              </IconContainer>
              <IconContainer onPress={onRecordHandler}>
                <RecordIcon
                  name={isRecording ? 'pause' : 'record'}
                  size={isRecording ? theme.text.s4 * 1.2 : theme.text.s5}
                />
              </IconContainer>
              <IconContainer
                disabled={Object.keys(tabFilters || {}).length === 0}
                onPress={openFilterModal}>
                <FilterIcon
                  name={'filterSolid'}
                  size={theme.text.s3}
                  color={'red'}
                />
              </IconContainer>
            </Row>
            <CircleItem
              isProgress
              onPress={onSeeAllPressHandler}
              isLoading={isLoading}
              onSeeAllPress={onSeeAllPressHandler}
              isSeeAllVisible={+(newsCount || '0') > 0}
              style={{
                marginTop: '5%',
                width: wdp(67),
                alignSelf: 'center',
              }}
              isError={isError}
              title={
                isError
                  ? tr('app.error')
                  : tabFilters === null && newsCount === '0'
                    ? tr('latest.resultAvailableTitleLoading')
                    : (newsCount || 0) + ' ' + tr('latest.resultAvailableTitle')
              }
              label={isError ? '' : tr('latest.resultAvailableDescription')}
            />
            <OptionsContainer>
              {optionsList.map(_renderOptionItem)}
            </OptionsContainer>
          </PaddingView>
          {(isRecommendation || !!staticRecommendation) && !isAppleEmail && (
            <Animated.View
              {...(Platform.OS === 'web'
                ? {className: 'screen-fade-in-down'}
                : {entering: FadeInDown, exiting: FadeOutDown})}>
              <BottomTabContainer
                outline
                outlineBackgroundColor={theme.text.light}
                label={
                  staticRecommendation
                    ? staticRecommendation?.name
                    : tr('news.recommendationTitle')
                }
                onPress={onBottomActionPress}
              />
            </Animated.View>
          )}
          <Space bottomInset={bottomInset} />
        </Container>
      </LinearGradient>
    </MainLayout>
  );
};
const mapStateToProps = (state: RootState) => {
  return {
    auth: state.auth,
    settings: state.settings,
    homeTabs: state.homeTabs,
  };
};

export default connect(mapStateToProps, null)(TabNews);
